import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { catColor, catLabel, sortCategories } from "@/lib/lore";
import { useEntities, useInvalidate } from "@/lib/queries";
import { LanguagePicker } from "@/components/language/SendToLanguage";

export function BulkSendDialog({ uid, onOpenChange }) {
  const invalidate = useInvalidate();
  const { data: entities = [] } = useEntities(uid);
  const [lid, setLid] = useState("");
  const [picked, setPicked] = useState({});
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [busy, setBusy] = useState(false);
  const cats = useMemo(() => sortCategories(new Set(entities.map((e) => e.category))), [entities]);
  const shown = entities.filter((e) => (!cat || e.category === cat) && (!q || e.name.toLowerCase().includes(q.toLowerCase())));
  const ids = Object.keys(picked).filter((k) => picked[k]);
  const allShown = shown.length > 0 && shown.every((e) => picked[e.id]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/languages/${lid}/words/from-entities`, { entity_ids: ids });
      invalidate(["words", lid], ["language", lid], ["languages", uid], "entity-words");
      toast.success(`${data.added} words added${data.skipped ? `, ${data.skipped} already in the dictionary` : ""}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="bulk-send-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">Send entries to a language</DialogTitle>
          <DialogDescription>Each ticked entry becomes a dictionary word (name → word, summary → meaning), linked back to its entry.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2"><Label>Language</Label><LanguagePicker uid={uid} value={lid} onChange={setLid} /></div>
        <div className="flex flex-wrap gap-1.5" data-testid="bulk-send-categories">
          {["", ...cats].map((c) => (
            <button key={c || "all"} type="button" onClick={() => setCat(c)} data-testid={`bulk-send-cat-${c || "all"}`}
              className={`rounded-sm border px-2.5 py-1 text-xs transition-colors ${cat === c ? "border-[#D97706] bg-[#D97706]/10 text-[#F59E0B]" : "border-[#2D3442] text-[#A8A090] hover:text-[#F3EBDD]"}`}>{c ? catLabel(c) : "All"}</button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name…" className="h-9" data-testid="bulk-send-search" />
          <label className="flex shrink-0 items-center gap-2 text-sm text-[#A8A090]">
            <Checkbox checked={allShown} onCheckedChange={(v) => setPicked({ ...picked, ...Object.fromEntries(shown.map((e) => [e.id, !!v])) })} data-testid="bulk-send-select-all" />Select all shown
          </label>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto lf-scroll rounded-md border border-[#2D3442] bg-[#0F131A] p-1.5" data-testid="bulk-send-list">
          {shown.map((e) => (
            <label key={e.id} className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 hover:bg-[#1A202C]">
              <Checkbox checked={!!picked[e.id]} onCheckedChange={(v) => setPicked({ ...picked, [e.id]: !!v })} data-testid={`bulk-send-entry-${e.id}`} />
              <span className="h-2 w-2 rounded-full" style={{ background: catColor(e.category) }} />
              <span className="text-sm text-[#F3EBDD]">{e.name}</span>
              <span className="truncate text-xs text-[#A8A090]">{e.summary}</span>
            </label>
          ))}
          {!shown.length && <p className="p-3 text-sm text-[#A8A090]">No entries match.</p>}
        </div>
        <DialogFooter><Button onClick={send} disabled={busy || !lid || !ids.length} data-testid="bulk-send-button">{busy ? "Sending…" : `Send ${ids.length} to dictionary`}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
